import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { XMLParser, XMLBuilder } from "fast-xml-parser";
import { Form, Button, Container, Accordion } from "react-bootstrap";
import { toast } from "react-toastify";
import axios from "axios";

const ScribeContentDisplay = forwardRef(
  ({ generatedContent, questionType, id, dataLoad, onHide, setIsLoading, isAdmin, hasEdit, canSaveEdit }, ref) => {
    const [quizData, setQuizData] = useState(null);
    const [editedData, setEditedData] = useState("");
    const canEdit = isAdmin || hasEdit;
    const hasSaveEdit = isAdmin || canSaveEdit;

    const handleDownload = async () => {
      if (!id || !quizData) {
        toast.error("Missing required data for download.");
        return;
      }

      const builder = new XMLBuilder({
        ignoreAttributes: false,
        attributeNamePrefix: "@_",
        format: true,
      });

      const xml = builder.build(quizData);
      const url = `${process.env.REACT_APP_API_URL}/scribe-bot`;
      const axiosConfig = {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      };

      try {
        await axios.put(`${url}-publish`, { id, content: xml }, axiosConfig);
        toast.success("Content saved successfully");
        setIsLoading(false);
        const params = new URLSearchParams({
          ids: `[${id}]`,
        });

        const { data: publishResponse } = await axios.get(
          `${url}?${params.toString()}`,
          undefined,
          axiosConfig,
        );
        const response = publishResponse.data;
        setEditedData(response[0].generated_content);
      } catch (error) {
        console.error("Error during download process:", error);
        toast.error("Download failed. Please try again.");
      } finally {
        if (dataLoad) dataLoad();
      }
    };

    useImperativeHandle(ref, () => ({
      callDisplayFunction: handleDownload,
    }));

    useEffect(() => {
      try {
        const content = editedData || generatedContent;
        if (content) {
          const parser = new XMLParser({
            ignoreAttributes: false,
            attributeNamePrefix: "@_",
            allowBooleanAttributes: true,
            preserveOrder: false,
          });

          const parsed = parser.parse(content);
          if (questionType === 1) {
            if (!parsed.quiz || (!parsed.quiz.question && typeof parsed !== "object")) {
              throw new Error("Invalid XML structure");
            }
          }
          if (parsed.quiz && parsed.quiz.question && !Array.isArray(parsed.quiz.question)) {
            parsed.quiz.question = [parsed.quiz.question];
          }

          if (parsed.quiz?.question) {
            parsed.quiz.question.forEach((q) => {
              if (Array.isArray(q.answer)) {
                q.answer.forEach((a) => {
                  if (typeof a.feedback === "string") {
                    a.feedback = { text: a.feedback };
                  }
                });
              }
            });
          }

          setQuizData(parsed);
        }
      } catch (error) {
        console.error("Error parsing XML content:", error);

        if (error.message === "Invalid XML structure") {
          toast.error("Something went wrong. Please try regenerating.");
        } else {
          toast.error("Something went wrong. Please try again later.");
        }
        if (onHide) {
          onHide();
        }
      }
    }, [generatedContent, editedData]);

    const updateQuestionName = (index, value) => {
      const updated = { ...quizData };
      if (!updated.quiz.question[index].name) {
        updated.quiz.question[index].name = { text: "" };
      }
      updated.quiz.question[index].name.text = value;
      setQuizData(updated);
    };

    const updateQuestionText = (index, value) => {
      const updated = { ...quizData };
      updated.quiz.question[index].questiontext.text = value;
      setQuizData(updated);
    };

    const updateAnswerText = (qIndex, aIndex, value) => {
      const updated = { ...quizData };
      updated.quiz.question[qIndex].answer[aIndex].text = value;
      setQuizData(updated);
    };

    const updateAnswerFeedback = (qIndex, aIndex, value) => {
      const updated = { ...quizData };
      updated.quiz.question[qIndex].answer[aIndex].feedback.text = value;
      setQuizData(updated);
    };

    const updateMatchingSubquestion = (qIndex, subIndex, field, value) => {
      const updated = { ...quizData };
      if (field === "question") {
        updated.quiz.question[qIndex].subquestion[subIndex].text = value;
      } else if (field === "answer") {
        updated.quiz.question[qIndex].subquestion[subIndex].answer.text = value;
      }
      setQuizData(updated);
    };

    const handleDeleteQuestion = (index) => {
      if (quizData.quiz.question.length <= 1) {
        toast.error("At least one question must remain.");
        return;
      }
      const updated = { ...quizData };
      updated.quiz.question.splice(index, 1);
      toast.success("Deleted successfully");
      setQuizData(updated);
    };

    if (!quizData) return <p>Loading...</p>;

    if (questionType === 0) {
      return (
        <div
          className="scribe-generated-content"
          dangerouslySetInnerHTML={{ __html: generatedContent }}
        />
      );
    }
    if (questionType === 1) {
      return (
        <>
          <Container className="mt-4">
            <Accordion>
              {quizData?.quiz?.question?.map((q, i) => (
                <Accordion.Item eventKey={i.toString()} key={i}>
                  <Accordion.Header>
                    Question {i + 1}: {q.name?.text || "Untitled"}
                  </Accordion.Header>
                  <Accordion.Body>
                    <Form.Group className="mb-3">
                      <Form.Label>Question Name:</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        disabled={!hasSaveEdit}
                        value={q.name?.text || ""}
                        onChange={(e) => updateQuestionName(i, e.target.value)}
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Question Text:</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        disabled={!hasSaveEdit}
                        value={q.questiontext.text}
                        onChange={(e) => updateQuestionText(i, e.target.value)}
                      />
                    </Form.Group>

                    {/* Answer/Feedback Accordion (non-matching) */}
                    {q["@_type"] !== "matching" && q.answer && (
                      <>
                        <Form.Label>Answers:</Form.Label>
                        <Accordion alwaysOpen>
                          {q.answer.map((a, ai) => (
                            <Accordion.Item eventKey={`${i}-a-${ai}`} key={ai}>
                              <Accordion.Header>
                                Answer {ai + 1}: {String(a.text).slice(0, 40)}...
                              </Accordion.Header>
                              <Accordion.Body>
                                <Form.Group className="mb-2">
                                  <Form.Label>Answer Text:</Form.Label>
                                  <Form.Control
                                    type="text"
                                    disabled={!hasSaveEdit}
                                    value={String(a.text)}
                                    onChange={(e) => updateAnswerText(i, ai, e.target.value)}
                                  />
                                </Form.Group>
                                <Form.Group>
                                  <Form.Label>Feedback:</Form.Label>
                                  <Form.Control
                                    as="textarea"
                                    rows={2}
                                    disabled={!hasSaveEdit}
                                    value={a.feedback?.text || ""}
                                    onChange={(e) => updateAnswerFeedback(i, ai, e.target.value)}
                                  />
                                </Form.Group>
                              </Accordion.Body>
                            </Accordion.Item>
                          ))}
                        </Accordion>
                      </>
                    )}

                    {/* Matching question Accordion */}
                    {q["@_type"] === "matching" &&
                      q.subquestion &&
                      Array.isArray(q.subquestion) && (
                        <>
                          <Form.Label>Matching Pairs:</Form.Label>
                          <Accordion alwaysOpen>
                            {q.subquestion.map((sub, si) => (
                              <Accordion.Item eventKey={`${i}-sub-${si}`} key={si}>
                                <Accordion.Header>
                                  Pair {si + 1}: {sub.text?.slice(0, 40)}...
                                </Accordion.Header>
                                <Accordion.Body>
                                  <Form.Group className="mb-2">
                                    <Form.Label>Question (Item):</Form.Label>
                                    <Form.Control
                                      type="text"
                                      placeholder="Item"
                                      disabled={!hasSaveEdit}
                                      value={sub.text}
                                      onChange={(e) =>
                                        updateMatchingSubquestion(i, si, "question", e.target.value)
                                      }
                                    />
                                  </Form.Group>
                                  <Form.Group>
                                    <Form.Label>Answer (Match):</Form.Label>
                                    <Form.Control
                                      type="text"
                                      placeholder="Match"
                                      disabled={!hasSaveEdit}
                                      value={sub.answer.text}
                                      onChange={(e) =>
                                        updateMatchingSubquestion(i, si, "answer", e.target.value)
                                      }
                                    />
                                  </Form.Group>
                                </Accordion.Body>
                              </Accordion.Item>
                            ))}
                          </Accordion>
                        </>
                      )}
                    {canEdit && (
                    <div className="d-flex justify-content-end mt-2">
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDeleteQuestion(i)}
                        className="px-3 py-1"
                      >
                        Delete
                      </Button>
                    </div>
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          </Container>
        </>
      );
    }

    return generatedContent;
  },
);

export default ScribeContentDisplay;
